import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentEquityData,
  type EnrollmentEquitySubmitInput,
  type EnrollmentEquitySubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment Equity data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentEquityData>
 * @example const response = await getEnrollmentEquityData()
 */
export const getEnrollmentEquityData = async (context?: DefaultContext): Promise<EnrollmentEquityData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentEquityData',
      query: `mutation getEnrollmentEquityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              experimentId
              userInfo {
                investment {
                  riskTolerance {
                    allocation {
                      equityPercent
                      fixedIncomePercent
                      cashEquivalentPercent
                    }
                  }
                }
              }
              recommendation {
                investment {
                  riskTolerance {
                    recommendedRiskTolerance
                    allowedRiskTolerances
                    riskTolerances {
                      category
                      index
                      allocation {
                        equityPercent
                        fixedIncomePercent
                        cashEquivalentPercent
                      }
                    }
                  }
                  equitySector {
                    type
                    maxAllocationPercent
                    recommendedRange {
                      recommendedPercent
                      allowedMinPercent
                      allowedMaxPercent
                    }
                  }
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentEquityData }
  return response.submitEnrollment
}

export const useGetEnrollmentEquityData = createQueryHook<undefined, EnrollmentEquityData>(getEnrollmentEquityData)

/**
 * Submit Enrollment Equity data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentEquitySubmitOutput>
 * @example const response = await submitEnrollmentEquityData();
 */
export const submitEnrollmentEquityData = async (
  enrollmentEquitySubmitInput: EnrollmentEquitySubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentEquitySubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentEquityData',
      query: `mutation submitEnrollmentEquityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentEquitySubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentEquitySubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentEquityData = createMutationHook<
  EnrollmentEquitySubmitInput,
  EnrollmentEquitySubmitOutput
>(submitEnrollmentEquityData)
